<template>
    <ul class="recommendations-list">
        <li
            v-for="recommendation in recommendationsAcceptedWithTextContent"
            :key="`recommendation--${recommendation.id}`"
            class="recommendations-list__item"
        >
            <RecommendationItem
                :is-edit-mode="true"
                :is-sample="false"
                :model-value="recommendation"
                :remove-dialog-ref="removeDialog"
                @remove="(id) => handleClickOnRemoveRecommendation(id)"
            />
        </li>
        <li v-if="recommendationsAcceptedWithTextContent.length === 0" class="recommendations-list__item">
            <RecommendationItem :is-edit-mode="true" :is-sample="true" :model-value="sampleRecommendation" />
        </li>
    </ul>
    <VJoyDialog
        id="removalRecommendationConfirmDialog"
        ref="removeDialog"
        :cancel-text="t('general.cancel')"
        :confirm-text="t('general.continue')"
        pattern-confirm
        @dialog:confirm="removeRecommendation"
    >
        <template #dialog-header>
            <p>{{ $t('generic.form.confirm.text') }}</p>
        </template>
    </VJoyDialog>
</template>
<script lang="ts" setup>
    import {ref, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyDialog} from '@maltjoy/core-vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {RecommendationDeletedBy} from '~/config/TrackingEvents';
    import {useProfileStore} from '~/store/profileStore';
    import {Recommendation} from '~/types/domain/Recommendations';
    import {useRecommendationsStore} from '../../store/recommendations.store';
    import RecommendationItem from '../RecommendationItem.vue';

    // Composables
    const {t} = useTranslation();
    const {trackRecommendationDeleted} = useProfileTracking();

    // Stores
    const {profile} = storeToRefs(useProfileStore());
    const recommendationsStore = useRecommendationsStore();
    const {recommendations, recommendationsAcceptedWithTextContent} = storeToRefs(recommendationsStore);

    // Refs
    const removeDialog = useTemplateRef('removeDialog');
    const recommendationIdToDelete = ref<string | undefined>();

    const sampleRecommendation = new Recommendation({
        accepted: true,
        accountId: '',
        authorCompanyName: t('profile.section.recommendation.eg.company'),
        authorFirstName: 'Georges',
        authorLastName: 'Abitbol',
        authorImgUrl: '//dam.malt.com/hop_profile_reco_eg_pp?w=50&h=50',
        recoText: t('profile.section.recommendation.eg.text', {0: profile.value!.firstname}),
        authorProfileUrl: '',
        experienceId: '',
        recommenderJob: '',
        id: '',
        recoTextUpdateDate: '1993-01-01',
        _links: [],
        hidden: false,
    });

    function handleClickOnRemoveRecommendation(id: string) {
        recommendationIdToDelete.value = id;
    }

    async function removeRecommendation() {
        const recoToDelete = recommendations.value!.recommendations.find((reco) => reco.id === recommendationIdToDelete.value)!;
        if (recoToDelete) {
            await recommendationsStore.deleteRecommendation(recoToDelete);
            trackRecommendationDeleted(profile.value!.profileId, RecommendationDeletedBy.FREELANCER, recoToDelete.experienceId);
            removeDialog.value?.hide();
            recommendationIdToDelete.value = undefined;
        }
    }
</script>
<style lang="scss" scoped>
    .recommendations-list {
        padding-right: 0;
    }
</style>
